import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Plus, Download, Eye, Trash2, ClipboardList, CheckCircle, XCircle, Camera } from 'lucide-react';
import PhotoUpload from '@/components/PhotoUpload';
import SignaturePad from '@/components/SignaturePad';
import { AS1851_ROUTINES, getChecklistForRoutine, getAllCategories } from '@/data/AS1851Checklists';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

// AS1851:2012 Asset-Specific Inspection Templates
const ASSET_INSPECTION_TEMPLATES = {
  'Fire Extinguisher': {
    '6-Monthly': [
      'Visual inspection - check for damage, corrosion, or leakage',
      'Check pressure gauge reading is in green zone',
      'Verify safety pin and tamper seal are intact',
      'Inspect hose and nozzle for blockage or damage',
      'Check operating instructions are legible',
      'Verify service tag is attached and current',
      'Ensure extinguisher is accessible and visible',
      'Check mounting bracket is secure',
      'Weigh cylinder to check for loss of agent',
      'Update maintenance record tag'
    ],
    'Annual': [
      'Complete all 6-monthly items',
      'Discharge test (sample basis)',
      'Internal examination if applicable',
      'Hydrostatic pressure test (if due)',
      'Refill or replace extinguishing agent',
      'Replace hose assembly if damaged',
      'Test gauge accuracy',
      'Apply new service tag with date',
      'Document test results'
    ]
  },
  'Fire Hose Reel': {
    '6-Monthly': [
      'Visual inspection of hose reel cabinet',
      'Check hose for damage, perishing, or deterioration',
      'Verify nozzle operates correctly',
      'Check hose guides for free movement',
      'Test manual shut-off valve operation',
      'Inspect drum for smooth rotation',
      'Check water flow and pressure',
      'Verify signage is visible and correct',
      'Inspect hose connections for leaks',
      'Update service tag'
    ],
    'Annual': [
      'Complete all 6-monthly items',
      'Full flow and pressure test',
      'Hose re-racking and storage check',
      'Internal valve inspection',
      'Hydrostatic hose test (if due)',
      'Replace hose if failed test',
      'Lubricate moving parts',
      'Check cabinet door operation'
    ]
  },
  'Fire Hydrant': {
    '6-Monthly': [
      'Visual inspection of hydrant and signage',
      'Check for physical damage or corrosion',
      'Verify hydrant is accessible',
      'Inspect outlet caps and threads',
      'Check valve gland for leaks',
      'Operate hydrant valve quarter turn',
      'Verify pressure gauge (if fitted)',
      'Check hose and nozzle condition',
      'Test coupling compatibility'
    ],
    'Annual': [
      'Complete all 6-monthly items',
      'Full flow and pressure test',
      'Operate valve through full range',
      'Test all outlets',
      'Internal valve inspection',
      'Pump performance test',
      'Check backflow prevention device',
      'Pressure test system',
      'Document all test results'
    ]
  },
  'Fire Sprinkler': {
    '6-Monthly': [
      'Visual inspection of sprinkler heads',
      'Check for correct orientation and clearance',
      'Inspect for corrosion, damage, or painting',
      'Verify no obstructions within 450mm radius',
      'Check sprinkler head escutcheons',
      'Inspect exposed piping',
      'Test pressure gauges',
      'Check alarm valve condition',
      'Verify water supply'
    ],
    'Annual': [
      'Complete all 6-monthly items',
      'Flow test alarm valve',
      'Test pressure switches',
      'Pump performance test',
      'Full system water flow test',
      'Check sprinkler head sample for corrosion',
      'Test fire brigade connections',
      'Valve operation checks',
      'Update system drawings if required'
    ]
  },
  'Fire Alarm Panel': {
    '6-Monthly': [
      'Visual inspection of panel and indicators',
      'Check all LED indicators functioning',
      'Test panel power supply',
      'Verify battery backup',
      'Check for fault indicators',
      'Test manual call points (sample)',
      'Verify alarm sounders operate',
      'Check detector zones (sample test)',
      'Inspect cabling for damage'
    ],
    'Annual': [
      'Complete all 6-monthly items',
      'Test all detectors (smoke, heat)',
      'Full system functional test',
      'Battery load test',
      'Test all manual call points',
      'Verify all alarm devices',
      'Check isolation functions',
      'Test monitoring system links',
      'Update system documentation'
    ]
  }
};

// General AS1851:2012 Checklists
const AS1851_CHECKLISTS = {
  '6-Monthly': [
    'Visual inspection of all fire protection equipment',
    'Check for physical damage or obstruction',
    'Verify equipment is accessible and signage visible',
    'Test operation of manual controls',
    'Check pressure gauges within operating range',
    'Inspect for damage, wear or deterioration',
    'Verify tamper seals are intact',
    'Check for leaks in system',
    'Verify maintenance tags are current'
  ],
  'Annual': [
    'Complete 6-monthly inspection items',
    'Discharge testing of equipment',
    'Flow and pressure testing',
    'Internal inspection of equipment',
    'Pump performance testing (if applicable)',
    'Valve operation testing',
    'Hydrostatic pressure testing',
    'Electrical system testing',
    'Full functional testing of all systems',
    'Update maintenance records and tags'
  ],
  'Defect Repair': [
    'Identify and document defect',
    'Assess severity and urgency',
    'Perform repair or replacement',
    'Test repaired equipment',
    'Verify compliance with standards',
    'Update maintenance records'
  ],
  'Installation': [
    'Verify design specifications',
    'Check all components installed correctly',
    'Test all system functions',
    'Commission system',
    'Provide operation and maintenance manuals',
    'Train client personnel',
    'Complete as-built documentation'
  ]
};

const ServiceReportsPage = () => {
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [registeredAssets, setRegisteredAssets] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [formData, setFormData] = useState({
    report_type: '',
    service_routine: '',
    asset_category: '',
    client_id: '',
    site_id: '',
    service_date: new Date().toISOString().split('T')[0],
    technician_name: '',
    technician_signature: '',
    overall_status: 'Pass',
    recommendations: '',
    next_service_due: '',
    notes: ''
  });
  const [assets, setAssets] = useState([]);
  const [checklist, setChecklist] = useState([]);
  const [showAssetSelector, setShowAssetSelector] = useState(false);

  useEffect(() => {
    fetchReports();
    fetchClients();
    fetchRegisteredAssets();
  }, []); // eslint-disable-line

  const fetchReports = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/service-reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      toast.error('Failed to fetch service reports');
    }
  };

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  const fetchRegisteredAssets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/assets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRegisteredAssets(response.data);
    } catch (error) {
      console.error('Failed to fetch assets');
    }
  };

  const handleReportTypeChange = (type) => {
    setFormData({ ...formData, report_type: type });
    // Update checklist for existing assets if they have asset-specific templates
    if (assets.length > 0 && assets[0].asset_type) {
      updateChecklistForAssetType(assets[0].asset_type, type);
    } else {
      const checklistItems = AS1851_CHECKLISTS[type] || [];
      setChecklist(checklistItems.map(item => ({ item, checked: false, notes: '' })));
    }
  };

  const updateChecklistForAssetType = (assetType, reportType) => {
    // Get asset-specific checklist if available
    const assetTemplate = ASSET_INSPECTION_TEMPLATES[assetType];
    if (assetTemplate && assetTemplate[reportType]) {
      const checklistItems = assetTemplate[reportType];
      setChecklist(checklistItems.map(item => ({ item, checked: false, notes: '' })));
    } else {
      // Fall back to general checklist
      const checklistItems = AS1851_CHECKLISTS[reportType] || [];
      setChecklist(checklistItems.map(item => ({ item, checked: false, notes: '' })));
    }
  };

  const handleClientChange = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setFormData({ ...formData, client_id: clientId, site_id: '' });
    setAssets([]); // Clear assets when client changes
  };

  const handleSiteChange = (siteId) => {
    setFormData({ ...formData, site_id: siteId });
    // Show assets registered to this site
    const siteAssets = registeredAssets.filter(asset => 
      asset.client_id === formData.client_id && asset.site_id === siteId
    );
    
    if (siteAssets.length > 0) {
      toast.success(`Found ${siteAssets.length} registered asset(s) for this site`);
      setShowAssetSelector(true);
    } else {
      toast.info('No registered assets found for this site. You can add assets manually.');
      setShowAssetSelector(false);
    }
  };

  const addAsset = () => {
    setAssets([...assets, {
      asset_type: '',
      asset_id: '',
      building: '',
      floor: '',
      room: '',
      location_notes: '',
      inspection_results: 'Pass',
      defects_found: '',
      photo_urls: [],
      create_defect_quote: false
    }]);
  };

  const updateAsset = (index, field, value) => {
    const updated = [...assets];
    updated[index][field] = value;
    setAssets(updated);
  };

  const removeAsset = (index) => {
    setAssets(assets.filter((_, i) => i !== index));
  };

  const updateChecklist = (index, field, value) => {
    const updated = [...checklist];
    updated[index][field] = value;
    setChecklist(updated);
  };

  const addChecklistItem = () => {
    setChecklist([...checklist, { item: '', compliant: true }]);
  };

  const handleRoutineChange = (routine) => {
    setFormData({ ...formData, service_routine: routine, asset_category: '' });
    setChecklist([]); // Clear checklist when routine changes
  };

  const handleCategoryChange = (category) => {
    setFormData({ ...formData, asset_category: category });
    // Load AS1851 checklist for this routine and category
    const routine = formData.service_routine;
    if (routine && category) {
      const as1851Checklist = getChecklistForRoutine(routine, category);
      if (as1851Checklist.length > 0) {
        setChecklist(as1851Checklist);
        toast.success(`Loaded AS1851:2012 ${routine} checklist for ${category}`);
      } else {
        setChecklist([]);
        toast.info('No standard checklist available. You can add custom items.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.report_type) {
      toast.error('Please select a report type');
      return;
    }
    
    if (!formData.service_date) {
      toast.error('Please select a service date');
      return;
    }
    
    if (!formData.client_id) {
      toast.error('Please select a client');
      return;
    }
    
    if (!formData.site_id) {
      toast.error('Please select a site');
      return;
    }
    
    if (!formData.technician_name || formData.technician_name.trim() === '') {
      toast.error('Please enter technician name');
      return;
    }
    
    if (!formData.overall_status) {
      toast.error('Please select overall status');
      return;
    }
    
    if (assets.length === 0) {
      toast.error('Please add at least one asset');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        assets_inspected: assets,
        checklist: checklist
      };
      
      console.log('Submitting service report:', submitData);
      
      const response = await axios.post(`${API}/service-reports`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Service report created:', response.data);
      toast.success('Service report created successfully');
      setIsDialogOpen(false);
      resetForm();
      await fetchReports();
    } catch (error) {
      console.error('Service report error:', error.response?.data || error);
      const errorMsg = error.response?.data?.detail || 'Failed to create service report';
      toast.error(errorMsg);
    }
  };

  const downloadPDF = async (reportId, reportNumber) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/service-reports/${reportId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service report?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/service-reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Service report deleted successfully');
      fetchReports();
    } catch (error) {
      toast.error('Failed to delete service report');
    }
  };

  const resetForm = () => {
    setFormData({
      report_type: '',
      client_id: '',
      site_id: '',
      service_date: new Date().toISOString().split('T')[0],
      technician_name: '',
      technician_signature: '',
      overall_status: 'Pass',
      recommendations: '',
      next_service_due: '',
      notes: ''
    });
    setAssets([]);
    setChecklist([]);
    setSelectedClient(null);
  };

  const getStatusBadge = (status) => {
    const colors = {
      'Pass': 'bg-green-500',
      'Pass with Defects': 'bg-yellow-500',
      'Fail': 'bg-red-500'
    };
    return <Badge className={colors[status] || 'bg-gray-500'}>{status}</Badge>;
  };

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white">Service Reports</h1>
            <p className="text-gray-300 mt-1">AS1851:2012 Compliant Service Reports</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700">
                <Plus className="w-4 h-4 mr-2" />
                Create Service Report
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 text-white border-gray-800 max-w-6xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Create Service Report</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>AS1851:2012 Service Routine</Label>
                    <Select
                      value={formData.service_routine}
                      onValueChange={handleRoutineChange}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select routine" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {Object.keys(AS1851_ROUTINES).map((key) => (
                          <SelectItem key={key} value={key}>
                            {AS1851_ROUTINES[key].label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.service_routine && (
                      <p className="text-xs text-gray-400 mt-1">
                        {AS1851_ROUTINES[formData.service_routine]?.description}
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Asset Category</Label>
                    <Select
                      value={formData.asset_category}
                      onValueChange={handleCategoryChange}
                      disabled={!formData.service_routine}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {getAllCategories().map((category) => (
                          <SelectItem key={category} value={category}>
                            {category}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formData.service_routine && formData.asset_category && checklist.length > 0 && (
                      <p className="text-xs text-green-400 mt-1">
                        ✓ AS1851:2012 checklist loaded ({checklist.length} items)
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Report Type</Label>
                    <Select
                      value={formData.report_type}
                      onValueChange={handleReportTypeChange}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="6-Monthly">6-Monthly Inspection</SelectItem>
                        <SelectItem value="Annual">Annual Inspection</SelectItem>
                        <SelectItem value="Defect Repair">Defect Repair</SelectItem>
                        <SelectItem value="Installation">Installation</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Service Date</Label>
                    <Input
                      type="date"
                      value={formData.service_date}
                      onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Client</Label>
                    <Select
                      value={formData.client_id}
                      onValueChange={handleClientChange}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select client" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {clients.map((client) => (
                          <SelectItem key={client.id} value={client.id}>
                            {client.client_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Site</Label>
                    <Select
                      value={formData.site_id}
                      onValueChange={handleSiteChange}
                      disabled={!selectedClient}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue placeholder="Select site" />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        {selectedClient?.sites.map((site, idx) => (
                          <SelectItem key={idx} value={site.site_name}>
                            {site.site_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Registered Assets from Selected Site */}
                {showAssetSelector && formData.site_id && (
                  <Card className="bg-gray-800 border-gray-700 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-white">Add Assets from Register</Label>
                      <Badge className="bg-blue-500">
                        {registeredAssets.filter(a => a.client_id === formData.client_id && a.site_id === formData.site_id).length} available
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                      {registeredAssets
                        .filter(regAsset => regAsset.client_id === formData.client_id && regAsset.site_id === formData.site_id)
                        .map((regAsset, idx) => (
                          <Button
                            key={idx}
                            type="button"
                            variant="outline"
                            className="justify-start text-left bg-gray-700 border-gray-600 hover:bg-gray-600"
                            onClick={() => {
                              const newAsset = {
                                asset_type: regAsset.asset_type,
                                asset_id: regAsset.id,
                                asset_tag: regAsset.asset_tag,
                                building: regAsset.building || '',
                                floor: regAsset.floor || '',
                                room: regAsset.room || '',
                                location_notes: regAsset.location_notes || '',
                                inspection_results: 'Pass',
                                defects_found: '',
                                photo_urls: [],
                                create_defect_quote: false
                              };
                              setAssets([...assets, newAsset]);
                              toast.success(`Added ${regAsset.asset_type} (${regAsset.asset_tag})`);
                            }}
                          >
                            <div>
                              <p className="font-medium text-white">{regAsset.asset_type}</p>
                              <p className="text-xs text-gray-400">{regAsset.asset_tag} - {[regAsset.building, regAsset.floor, regAsset.room].filter(Boolean).join(', ')}</p>
                            </div>
                          </Button>
                        ))}
                    </div>
                  </Card>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Technician Name</Label>
                    <Input
                      value={formData.technician_name}
                      onChange={(e) => setFormData({ ...formData, technician_name: e.target.value })}
                      required
                      className="bg-gray-800 border-gray-700"
                    />
                  </div>
                  <div>
                    <Label>Overall Status</Label>
                    <Select
                      value={formData.overall_status}
                      onValueChange={(value) => setFormData({ ...formData, overall_status: value })}
                    >
                      <SelectTrigger className="bg-gray-800 border-gray-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-gray-800 border-gray-700">
                        <SelectItem value="Pass">Pass</SelectItem>
                        <SelectItem value="Pass with Defects">Pass with Defects</SelectItem>
                        <SelectItem value="Fail">Fail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Assets Inspected */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Assets Inspected</Label>
                    <Button type="button" size="sm" onClick={addAsset} className="bg-blue-600 hover:bg-blue-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Asset
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {assets.map((asset, idx) => (
                      <Card key={idx} className="bg-gray-800 border-gray-700 p-4">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <h4 className="text-white font-medium flex items-center gap-2">
                              <Camera className="w-4 h-4 text-orange-500" />
                              Asset {idx + 1}
                            </h4>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => removeAsset(idx)}
                              className="text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="Asset Type"
                              value={asset.asset_type}
                              onChange={(e) => updateAsset(idx, 'asset_type', e.target.value)}
                              className="bg-gray-700 border-gray-600"
                            />
                            <Input
                              placeholder="Asset ID"
                              value={asset.asset_id}
                              onChange={(e) => updateAsset(idx, 'asset_id', e.target.value)}
                              className="bg-gray-700 border-gray-600"
                            />
                          </div>

                          <div className="grid grid-cols-3 gap-2">
                            <Input
                              placeholder="Building"
                              value={asset.building}
                              onChange={(e) => updateAsset(idx, 'building', e.target.value)}
                              className="bg-gray-700 border-gray-600"
                            />
                            <Input
                              placeholder="Floor"
                              value={asset.floor}
                              onChange={(e) => updateAsset(idx, 'floor', e.target.value)}
                              className="bg-gray-700 border-gray-600"
                            />
                            <Input
                              placeholder="Room"
                              value={asset.room}
                              onChange={(e) => updateAsset(idx, 'room', e.target.value)}
                              className="bg-gray-700 border-gray-600"
                            />
                          </div>

                          <Input
                            placeholder="Location Notes"
                            value={asset.location_notes}
                            onChange={(e) => updateAsset(idx, 'location_notes', e.target.value)}
                            className="bg-gray-700 border-gray-600"
                          />

                          <div className="flex gap-2">
                            <Select
                              value={asset.inspection_results}
                              onValueChange={(value) => updateAsset(idx, 'inspection_results', value)}
                            >
                              <SelectTrigger className="bg-gray-700 border-gray-600">
                                <SelectValue placeholder="Inspection Result" />
                              </SelectTrigger>
                              <SelectContent className="bg-gray-800 border-gray-700">
                                <SelectItem value="Pass">Pass</SelectItem>
                                <SelectItem value="Fail">Fail</SelectItem>
                                <SelectItem value="Requires Attention">Requires Attention</SelectItem>
                              </SelectContent>
                            </Select>
                            
                            {(asset.inspection_results === 'Fail' || asset.inspection_results === 'Requires Attention') && (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={asset.create_defect_quote}
                                  onCheckedChange={(checked) => updateAsset(idx, 'create_defect_quote', checked)}
                                />
                                <span className="text-sm text-gray-300">Create defect</span>
                              </div>
                            )}
                          </div>
                          
                          {(asset.inspection_results === 'Fail' || asset.inspection_results === 'Requires Attention') && (
                            <Textarea
                              placeholder="Defects Found (describe the issue)"
                              value={asset.defects_found}
                              onChange={(e) => updateAsset(idx, 'defects_found', e.target.value)}
                              className="bg-gray-700 border-gray-600"
                              rows={2}
                            />
                          )}

                          <div>
                            <Label className="text-sm mb-2">Inspection Photos</Label>
                            <PhotoUpload
                              photos={asset.photo_urls || []}
                              onChange={(photos) => updateAsset(idx, 'photo_urls', photos)}
                              maxPhotos={5}
                            />
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>

                {/* AS1851:2012 Checklist */}
                {checklist.length > 0 && (
                  <div>
                    <Label className="mb-2">AS1851:2012 Checklist</Label>
                    <Card className="bg-gray-800 border-gray-700 p-4">
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {checklist.map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3 p-2 hover:bg-gray-700 rounded">
                            <Checkbox
                              checked={item.checked}
                              onCheckedChange={(checked) => updateChecklist(idx, 'checked', checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className="text-sm text-gray-300">{item.item}</p>
                              <Input
                                placeholder="Notes (optional)"
                                value={item.notes}
                                onChange={(e) => updateChecklist(idx, 'notes', e.target.value)}
                                className="bg-gray-700 border-gray-600 mt-1 text-xs"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Recommendations</Label>
                    <Textarea
                      value={formData.recommendations}
                      onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                      className="bg-gray-800 border-gray-700"
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Next Service Due</Label>
                    <Input
                      type="date"
                      value={formData.next_service_due}
                      onChange={(e) => setFormData({ ...formData, next_service_due: e.target.value })}
                      className="bg-gray-800 border-gray-700"
                    />
                    <Label className="mt-2">Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-gray-800 border-gray-700"
                      rows={2}
                    />
                  </div>
                </div>

                {/* Electronic Signature */}
                <div>
                  <SignaturePad
                    value={formData.technician_signature}
                    onChange={(signature) => setFormData({ ...formData, technician_signature: signature })}
                    label="Technician Signature"
                  />
                </div>

                <div className="flex gap-2 justify-end pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-orange-600 to-red-600">
                    Create Report
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reports List */}
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className="bg-white/5 border-white/10">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-orange-500/20 p-3 rounded-lg">
                      <ClipboardList className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <CardTitle className="text-white">{report.report_number}</CardTitle>
                      <p className="text-sm text-gray-400 mt-1">{report.client_name} - {report.site_name}</p>
                      <p className="text-xs text-gray-500 mt-1">{report.report_type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {getStatusBadge(report.overall_status)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <span className="text-gray-400 text-sm">Service Date:</span>
                    <p className="text-white">{report.service_date}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Technician:</span>
                    <p className="text-white">{report.technician_name}</p>
                  </div>
                  <div>
                    <span className="text-gray-400 text-sm">Assets Inspected:</span>
                    <p className="text-white">{report.assets_inspected.length} item(s)</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => downloadPDF(report.id, report.report_number)}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download PDF
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(report.id)}
                    className="text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {reports.length === 0 && (
          <div className="text-center py-12">
            <ClipboardList className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <p className="text-gray-400">No service reports yet. Create your first report to get started.</p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default ServiceReportsPage;
